import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Reveal from "@/components/motion/Reveal";
import KineticLines from "@/components/motion/KineticLines";
import { fetchAnimals } from "@/lib/api";
import { Leaf, Mushroom, Snake, Bat, Rose } from "@/components/decor/SvgIcons";

const BLOB_CLASSES = ["blob-a", "blob-b", "blob-c", "blob-d"];

// Rotating tinted card backgrounds — pale, palette-consistent
const CARD_TINTS = [
  "linear-gradient(140deg, #E8DFF6 0%, #F5EFD8 100%)",   // lavender → cream
  "linear-gradient(140deg, #DDE9C7 0%, #F5EFD8 100%)",   // sage → cream
  "linear-gradient(140deg, #F3D9B8 0%, #F5EFD8 100%)",   // warm gold → cream
  "linear-gradient(140deg, #F0CFD6 0%, #F5EFD8 100%)",   // dusty rose → cream
];

export default function Animals() {
  const [animals, setAnimals] = useState([]);

  useEffect(() => {
    fetchAnimals().then(setAnimals).catch(() => {});
  }, []);

  return (
    <div className="pt-28">
      <section className="relative overflow-hidden pb-10">
        <div className="absolute top-8 right-8 opacity-70"><Snake size={90} className="rotate-12" /></div>
        <div className="absolute top-40 left-4 opacity-60"><Mushroom size={70} /></div>
        <div className="absolute top-16 left-1/2 opacity-80 hidden md:block"><Bat size={54} /></div>
        <div className="absolute top-56 right-1/3 opacity-70 hidden md:block"><Rose size={56} /></div>

        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="chip mb-6">Meet the Cottage</div>
          <KineticLines
            lines={["Meet our animals,\u00A0", "the whole crew."]}
            italicIdx={1}
            className="font-display text-forest text-[12vw] md:text-[7vw] lg:text-[6.2rem] leading-[0.96] tracking-tight"
          />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-lg text-forest/80">
              Every animal here is either rescued or from a local, ethical breeder — and each one
              is a member of our family. Hand-raised, socialized, and calm around curious humans of
              all sizes, they can't wait to say hello.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pt-4 pb-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="swash my-6" />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="animals-grid">
            {animals.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.06 }}
                className="grim-card p-6"
                style={{ backgroundImage: CARD_TINTS[i % CARD_TINTS.length] }}
                data-testid={`animal-card-${a.id}`}
              >
                <div className="relative">
                  {a.is_new && (
                    <div
                      className="chip ribbon-new absolute top-3 left-3 z-10 rotate-[-6deg] shadow-lg"
                      data-testid={`new-ribbon-${a.id}`}
                    >
                      New to the cottage
                    </div>
                  )}
                  <img
                    src={a.image}
                    alt={a.name}
                    loading="lazy"
                    className={`${BLOB_CLASSES[i % 4]} w-full aspect-square object-cover border-4 border-cream shadow-lg`}
                  />
                  <div className="absolute -bottom-2 -right-2 chip !bg-forest !text-cream !border-forest">{a.category}</div>
                </div>
                <div className="mt-6">
                  <div className="flex items-baseline justify-between">
                    <h3 className="font-display text-3xl text-forest">{a.name}</h3>
                    <div
                      className="font-display text-2xl text-plum"
                      title={a.sex ? (a.sex === "female" ? "Female" : "Male") : undefined}
                      data-testid={a.sex ? `sex-symbol-${a.id}` : undefined}
                    >
                      {a.sex ? (a.sex === "female" ? "♀" : "♂") : "·"}
                    </div>
                  </div>
                  <div className="text-forest/70 italic">{a.species}</div>
                  <p className="mt-3 text-forest/80">{a.personality}</p>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl bg-sage/25 p-3">
                      <div className="text-[10px] tracking-widest uppercase text-forest/60">Favorite food</div>
                      <div className="text-forest">{a.favorite_food}</div>
                    </div>
                    <div className="rounded-xl bg-lavender/25 p-3">
                      <div className="text-[10px] tracking-widest uppercase text-forest/60">Enrichment</div>
                      <div className="text-forest">{a.favorite_enrichment}</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-start gap-2 text-forest/85">
                    <Leaf size={18} />
                    <p className="text-sm italic">{a.fun_fact}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
